<template>
  <div class="hub-workspace-header-root">
    <!-- ── STANDARD MS PAGE HEADER ── -->
    <div class="ms-page-header mb-4">
      <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
        <!-- Left: Back Button, Platform Icon & Breadcrumb/Title -->
        <div class="d-flex align-items-center gap-3 flex-wrap min-w-0">
          <button
            type="button"
            class="btn btn-outline-info btn-sm fw-bold px-3 py-2 shadow-xs back-btn d-flex align-items-center"
            @click="$emit('back-to-hub')"
            title="Return to Platforms Grid View"
          >
            <i class="fa-solid fa-arrow-left me-2"></i>
            <span>All Platforms</span>
          </button>

          <div
            class="ms-page-header__icon flex-shrink-0"
            :style="iconBoxStyle"
          >
            <img v-if="isUrl(platform.icon)" :src="platform.icon" class="hub-header-icon-img" alt="" />
            <i v-else :class="platform.icon || 'fa-solid fa-shapes'"></i>
          </div>

          <div class="min-w-0">
            <p class="ms-page-header__breadcrumb">
              Platform Delivery <span class="ms-breadcrumb-sep">/</span> Unified Platform Hub <span class="ms-breadcrumb-sep">/</span> {{ platform.name }}
            </p>
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <h1 class="ms-page-header__title">{{ platform.name }}</h1>
              <span class="badge bg-light text-secondary border font-monospace px-2 py-1">{{ platform.key }}</span>

              <!-- Platform Switcher Dropdown -->
              <div class="dropdown d-inline-block ms-1">
                <button
                  class="btn btn-sm btn-header-secondary d-flex align-items-center gap-2 dropdown-toggle"
                  type="button"
                  id="hubPlatformDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  title="Switch to another managed platform"
                >
                  <span class="hub-color-dot" :style="{ backgroundColor: platform.primary_color || '#00a0c1' }"></span>
                  <span>Switch Platform</span>
                </button>
                <ul class="dropdown-menu dropdown-menu-start shadow border-0 p-2 rounded-3" aria-labelledby="hubPlatformDropdown" style="min-width: 260px; max-height: 380px; overflow-y: auto; z-index: 1060;">
                  <li class="dropdown-header text-muted-custom text-xxs text-uppercase fw-bold px-2 py-1">All Platforms</li>
                  <li v-for="plat in platforms" :key="plat.id">
                    <a
                      class="dropdown-item rounded-2 py-2 px-2 text-xs d-flex align-items-center justify-content-between"
                      :class="{ 'active bg-cyan-primary text-white': plat.key === platform.key }"
                      href="#"
                      @click.prevent="$emit('switch-platform', plat)"
                    >
                      <div class="d-flex align-items-center gap-2 min-w-0">
                        <span class="hub-color-dot" :style="{ backgroundColor: plat.primary_color || '#00a0c1' }"></span>
                        <span class="fw-bold text-truncate">{{ plat.name }}</span>
                      </div>
                      <span
                        class="badge border font-monospace text-xxs ms-2"
                        :class="plat.key === platform.key ? 'bg-white text-dark' : 'bg-light text-secondary'"
                      >
                        {{ plat.key }}
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: My Atlassian Token & Platform Settings Full-Sized Buttons -->
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <router-link
            to="/platform-settings/user-settings"
            class="btn btn-header-secondary fw-bold d-flex align-items-center gap-2 px-3 py-2 text-sm text-decoration-none shadow-xs"
            title="Configure your personal Atlassian API token in User Settings"
          >
            <i class="fa-solid fa-key text-info"></i>
            <span>My Atlassian Token</span>
          </router-link>

          <button
            type="button"
            class="btn btn-cyan-primary fw-bold d-flex align-items-center gap-2 px-3 py-2 text-sm shadow-xs"
            @click="$emit('open-settings', { platform, initialTab: activeModule })"
            title="Configure platform settings across all modules"
          >
            <i class="fa-solid fa-gear"></i>
            <span>Platform Settings</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── ORGANISATIONS-STYLE TAB CARD FOR MODULE SWITCHER ── -->
    <div class="ms-card mb-4">
      <div class="org-tabs-container">
        <div class="org-tabs-nav">
          <button
            type="button"
            class="org-tab-btn"
            :class="{ active: activeModule === 'regression' }"
            @click="$emit('switch-module', 'regression')"
          >
            <i class="fa-solid fa-list-check me-2"></i>
            <span>Regression Library</span>
            <span class="org-tab-counter-badge">{{ regressionBadgeCount }}</span>
          </button>
          <button
            type="button"
            class="org-tab-btn"
            :class="{ active: activeModule === 'bug_report' }"
            @click="$emit('switch-module', 'bug_report')"
          >
            <i class="fa-solid fa-bug me-2"></i>
            <span>Bug Report</span>
            <span class="org-tab-counter-badge">{{ bugBadgeCount }}</span>
          </button>
          <button
            type="button"
            class="org-tab-btn"
            :class="{ active: activeModule === 'releases' }"
            @click="$emit('switch-module', 'releases')"
          >
            <i class="fa-solid fa-rocket me-2"></i>
            <span>Releases</span>
            <span class="org-tab-counter-badge">{{ releaseBadgeText }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HubWorkspaceHeader",
  props: {
    platform: {
      type: Object,
      required: true
    },
    platforms: {
      type: Array,
      default: () => []
    },
    activeModule: {
      type: String,
      default: "regression"
    },
    stats: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ["back-to-hub", "switch-platform", "switch-module", "open-settings"],
  computed: {
    iconBoxStyle() {
      const color = this.platform.primary_color || "#00a0c1";
      const isCustomUrl = this.isUrl(this.platform.icon);
      return {
        backgroundColor: isCustomUrl ? "#ffffff" : this.hexToRgba(color, 0.12),
        color: color,
        border: isCustomUrl ? "1px solid #e2e8f0" : `1px solid ${this.hexToRgba(color, 0.28)}`
      };
    },
    regressionBadgeCount() {
      return this.stats?.regression?.tests ?? this.platform.total_tests ?? 0;
    },
    bugBadgeCount() {
      return this.stats?.bug_report?.open ?? this.platform.open_bugs_count ?? this.platform.open_bugs ?? 0;
    },
    releaseBadgeText() {
      return this.stats?.releases?.latest_version || this.platform.latest_version || "v1.0";
    }
  },
  methods: {
    isUrl(val) {
      return typeof val === "string" && (val.startsWith("http://") || val.startsWith("https://") || val.startsWith("/"));
    },
    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(0, 160, 193, ${alpha})`;
      const clean = hex.replace("#", "");
      let r, g, b;
      if (clean.length === 3) {
        r = parseInt(clean[0] + clean[0], 16);
        g = parseInt(clean[1] + clean[1], 16);
        b = parseInt(clean[2] + clean[2], 16);
      } else if (clean.length === 6) {
        r = parseInt(clean.substring(0, 2), 16);
        g = parseInt(clean.substring(2, 4), 16);
        b = parseInt(clean.substring(4, 6), 16);
      } else {
        return `rgba(0, 160, 193, ${alpha})`;
      }
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
};
</script>

<style scoped>
/* Page Header */
.ms-page-header {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
  border: 1px solid rgba(0, 130, 152, 0.15);
  border-left: 5px solid #008298;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ms-page-header__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(0, 130, 152, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  color: #008298;
}

.ms-page-header__breadcrumb {
  font-size: 0.76rem;
  color: #557d84;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}

.ms-breadcrumb-sep {
  margin: 0 6px;
  color: rgba(0, 130, 152, 0.3);
}

.ms-page-header__title {
  font-size: 1.45rem;
  font-weight: 800;
  color: #00363f;
  margin: 0;
  letter-spacing: -0.3px;
}

.back-btn {
  background-color: #ffffff !important;
  border-color: rgba(0, 130, 152, 0.3) !important;
  color: #008298 !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.back-btn:hover,
.back-btn:focus,
.back-btn:active {
  background: linear-gradient(135deg, #008298, #005c6b) !important;
  border-color: #005c6b !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(0, 130, 152, 0.2) !important;
}

.hub-header-icon-img {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.hub-color-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

/* ── Header Secondary Button ── */
.btn-header-secondary {
  background-color: #ffffff !important;
  color: #334155 !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
}

.btn-header-secondary:hover,
.btn-header-secondary:focus,
.btn-header-secondary:active {
  background-color: #f1f5f9 !important;
  color: #0f172a !important;
  border-color: #94a3b8 !important;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.06) !important;
}

.btn-header-secondary:hover i,
.btn-header-secondary:focus i,
.btn-header-secondary:active i {
  color: #008ca9 !important;
}

.btn-header-secondary:hover span,
.btn-header-secondary:focus span,
.btn-header-secondary:active span {
  color: #0f172a !important;
}

/* ── Primary Cyan Button ── */
.btn-cyan-primary {
  background-color: #00A0C1 !important;
  color: #ffffff !important;
  border: 1px solid #00A0C1 !important;
  border-radius: 8px !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.btn-cyan-primary:hover,
.btn-cyan-primary:focus,
.btn-cyan-primary:active {
  background-color: #0891b2 !important;
  border-color: #0891b2 !important;
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.25) !important;
}

/* ── ORGANISATIONS DASHBOARD TAB NAVIGATION ── */
.ms-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.org-tabs-container {
  background-color: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.org-tabs-nav {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.org-tab-btn {
  background: none;
  border: none;
  padding: 16px 20px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  outline: none;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  user-select: none;
}

.org-tab-btn:hover {
  color: #00768a;
}

.org-tab-btn.active {
  color: #00768a;
  font-weight: 700;
}

.org-tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #00768a;
  border-radius: 3px 3px 0 0;
}

.org-tab-counter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  margin-left: 8px;
  background-color: #e2e8f0;
  color: #475569;
  transition: all 0.2s ease;
}

.org-tab-btn.active .org-tab-counter-badge {
  background-color: #00768a;
  color: #ffffff;
}

.text-dark-blue {
  color: #0f172a !important;
}

.text-muted-custom {
  color: #64748b !important;
}

.text-xxs {
  font-size: 0.6875rem !important;
}

.bg-cyan-primary {
  background-color: #00A0C1 !important;
}
</style>
